import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Undo2, Redo2, Save, Download, Loader2, Monitor, Tablet, Smartphone } from 'lucide-react'
import { Button, Badge } from '@trafiflow/shared-ui'
import { TenantSelector } from '../TenantSelector'
import type { SaveStatus, DevicePreview } from '../../types'

interface BuilderToolbarProps {
  funnelId?: string
  stepId?: string
  blockCount: number
  saveStatus: SaveStatus
  showPreview: boolean
  devicePreview: DevicePreview
  canUndo: boolean
  canRedo: boolean
  onUndo: () => void
  onRedo: () => void
  onSave: () => void
  onExport: () => void
  onTogglePreview: () => void
  onDeviceChange: (device: DevicePreview) => void
}

const SAVE_LABELS: Record<SaveStatus, { text: string; className: string }> = {
  idle: { text: '', className: '' },
  unsaved: { text: 'Unsaved changes', className: 'text-tf-feedback-warning-text' },
  saving: { text: 'Saving...', className: 'text-blue-500' },
  saved: { text: 'Saved', className: 'text-green-600' },
  error: { text: 'Save failed', className: 'text-red-500' },
}

const DEVICES: { value: DevicePreview; icon: typeof Monitor; label: string }[] = [
  { value: 'desktop', icon: Monitor, label: 'Desktop' },
  { value: 'tablet', icon: Tablet, label: 'Tablet' },
  { value: 'mobile', icon: Smartphone, label: 'Mobile' },
]

export function BuilderToolbar({
  funnelId,
  stepId,
  blockCount,
  saveStatus,
  showPreview,
  devicePreview,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
  onExport,
  onTogglePreview,
  onDeviceChange,
}: BuilderToolbarProps) {
  const navigate = useNavigate()

  return (
    <div className="flex items-center justify-between px-4 py-2 border-b border-default-200 bg-content1">
      <div className="flex items-center gap-3">
        <Button variant="secondary" className="text-sm" onClick={() => navigate(`/funnels/${funnelId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div>
          <h1 className="text-sm font-semibold text-foreground">Page Builder</h1>
          <p className="text-xs text-default-500">Step: {stepId || 'New'}</p>
        </div>
        <TenantSelector compact />
        {saveStatus !== 'idle' && (
          <span className={`text-xs font-medium ${SAVE_LABELS[saveStatus].className}`}>
            {SAVE_LABELS[saveStatus].text}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Device toggle */}
        <div className="flex items-center border border-default-200 rounded-lg overflow-hidden">
          {DEVICES.map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              className={`px-2 py-1.5 transition-colors ${devicePreview === value ? 'bg-primary/10 text-primary' : 'text-default-500 hover:bg-content2'}`}
              onClick={() => onDeviceChange(value)}
              title={label}
            >
              <Icon className="w-4 h-4" />
            </button>
          ))}
        </div>

        {/* Undo/Redo */}
        <div className="flex items-center border border-default-200 rounded-lg overflow-hidden">
          <button className="px-2 py-1.5 text-default-500 hover:bg-content2 disabled:opacity-30" onClick={onUndo} disabled={!canUndo} title="Undo">
            <Undo2 className="w-4 h-4" />
          </button>
          <button className="px-2 py-1.5 text-default-500 hover:bg-content2 disabled:opacity-30 border-l border-default-200" onClick={onRedo} disabled={!canRedo} title="Redo">
            <Redo2 className="w-4 h-4" />
          </button>
        </div>

        <Button variant="secondary" className="text-sm" onClick={onSave} disabled={saveStatus === 'saving' || saveStatus === 'idle'}>
          {saveStatus === 'saving' ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
          Save
        </Button>
        <Button variant="secondary" className="text-sm" onClick={onExport} title="Export JSON">
          <Download className="w-4 h-4 mr-1" /> Export
        </Button>
        <Button variant="secondary" className="text-sm" onClick={onTogglePreview}>
          {showPreview ? <EyeOff className="w-4 h-4 mr-1" /> : <Eye className="w-4 h-4 mr-1" />}
          {showPreview ? 'Edit' : 'Preview'}
        </Button>
        <Badge variant="info">{blockCount} blocks</Badge>
      </div>
    </div>
  )
}
